/* External */
import { useCallback, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import FocusableButton from '../FocusableButton/FocusableButton';
import CustomKey from './CustomKey';
import FocusableDiv from '../FocusableDiv/FocusableDiv';

/* Utils */
import { noOp } from '../../utils/functions';
import {
  customKeyboardBR,
  uppercaseKeyboardBR,
  variationKeyboardBR,
  uppercaseVariationKeyboardBR,
  symbolsKeyboardBR,
  customKeyboardES,
  uppercaseKeyboardES,
} from './utils';
import withCursorVisibilityFilterFocusable from '../../hocs/withCursorVisibilityFilter.hoc';

/* Assets */
import CapsLock from '../../assets/svg/caps-lock.svg';
import CapsLockOn from '../../assets/svg/caps-lock-on.svg';
import Space from '../../assets/svg/space.svg';
import Delete from '../../assets/svg/delete.svg';

/* Styles */
import './DefaultKeyboard.scss';

/* Others */
import { useIsBrazil } from '../../providers/ConfigProvider';

function DefaultKeyboard({
  className,
  confirm,
  deleteCharacter,
  addSpace,
  addCharacter,
}) {
  const brProfile = useIsBrazil();
  const [keyboard] = useState({
    custom: brProfile ? customKeyboardBR : customKeyboardES,
    uppercase: brProfile ? uppercaseKeyboardBR : uppercaseKeyboardES,
    variation: variationKeyboardBR,
    uppercaseVariation: uppercaseVariationKeyboardBR,
    symbols: symbolsKeyboardBR,
  });

  const [state, setState] = useState({
    letters: keyboard.custom,
    uppercase: false,
    variation: false,
    symbols: false,
  });

  const uppercaseCharacter = useCallback(() => {
    setState((currentState) => {
      if (currentState.uppercase && currentState.variation === false) {
        return {
          ...currentState,
          letters: keyboard.custom,
          uppercase: false,
          symbols: false,
        };
      }

      if (currentState.uppercase === false && currentState.variation === false) {
        return {
          ...currentState,
          letters: keyboard.uppercase,
          uppercase: true,
          symbols: false,
        };
      }

      if (currentState.uppercase === false && currentState.variation) {
        return {
          ...currentState,
          letters: keyboard.uppercaseVariation,
          uppercase: true,
          symbols: false,
        };
      }

      if (currentState.uppercase && currentState.variation) {
        return {
          ...currentState,
          letters: keyboard.variation,
          uppercase: false,
          symbols: false,
        };
      }

      return currentState;
    });
  }, [keyboard]);

  const variationCharacter = useCallback(() => {
    setState((currentState) => {
      if (!currentState.variation) {
        return {
          ...currentState,
          letters: keyboard.variation,
          variation: true,
          uppercase: false,
          symbols: false,
        };
      }

      if (currentState.variation) {
        return {
          ...currentState,
          letters: keyboard.custom,
          variation: false,
          uppercase: false,
          symbols: false,
        };
      }
      return currentState;
    });
  }, [keyboard]);

  const symbolCharacter = useCallback(() => {
    setState((currentState) => {
      if (!currentState.symbols) {
        return {
          ...currentState,
          letters: keyboard.symbols,
          variation: false,
          uppercase: false,
          symbols: true,
        };
      }

      if (currentState.symbols) {
        return {
          ...currentState,
          letters: keyboard.custom,
          variation: false,
          uppercase: false,
          symbols: false,
        };
      }

      return currentState;
    });
  }, [keyboard]);

  return (
    <div className={`dtv-html5-c-profile-keyboard ${className}`}>
      <FocusableDiv className="dtv-html5-c-profile-keyboard__letters">
        {
          state.letters.map((letter) => (
            <CustomKey
              key={letter.character}
              character={letter.character}
              variation={letter.variation}
              onPress={addCharacter}
              className="dtv-html5-c-profile-keyboard__letter"
            />
          ))
        }
      </FocusableDiv>
      <FocusableDiv className="dtv-html5-c-profile-keyboard__action-buttons">
        <FocusableButton
          onEnterPress={uppercaseCharacter}
          className="dtv-html5-c-profile-keyboard__action-button"
        >
          {state.uppercase
            ? (<img src={getJsonImage('caps-lock-on') || CapsLockOn} alt="Caps Lock On" />)
            : (<img src={getJsonImage('caps-lock') || CapsLock} alt="Caps Lock" />)}
        </FocusableButton>
        <FocusableButton
          onEnterPress={symbolCharacter}
          className="dtv-html5-c-profile-keyboard__action-button"
        >
          {state.symbols === false ? '#$%' : 'ABC'}
        </FocusableButton>
        <FocusableButton
          onEnterPress={variationCharacter}
          className="dtv-html5-c-profile-keyboard__action-button"
        >
          {state.variation === false ? 'à' : 'ABC'}
        </FocusableButton>
        <FocusableButton
          onEnterPress={addSpace}
          className="dtv-html5-c-profile-keyboard__action-button space-button"
        >
          <img src={getJsonImage('space') || Space} alt="Space" />
        </FocusableButton>
        <FocusableButton
          onEnterPress={deleteCharacter}
          className="dtv-html5-c-profile-keyboard__action-button"
        >
          <img src={getJsonImage('delete') || Delete} alt="Delete" />
        </FocusableButton>
        <FocusableButton
          onEnterPress={confirm}
          className="dtv-html5-c-profile-keyboard__action-button accept-button"
        >
          <FormattedMessage id="common.accept" />
        </FocusableButton>
      </FocusableDiv>
    </div>
  );
}

DefaultKeyboard.propTypes = {
  className: PropTypes.string,
  confirm: PropTypes.func,
  deleteCharacter: PropTypes.func,
  addSpace: PropTypes.func,
  addCharacter: PropTypes.func,
};

DefaultKeyboard.defaultProps = {
  className: '',
  confirm: noOp,
  deleteCharacter: noOp,
  addSpace: noOp,
  addCharacter: noOp,
};

export default memo(withCursorVisibilityFilterFocusable()(memo(DefaultKeyboard)));
